<template>

   <div class="pentest-field-group">
      <label for="extIntType" class="form-label">Is this an internal (office) and/or external (cloud infrastructure)
         penetration test?
         <span v-if="isCreate" class="requiredField">*</span>
      </label>

      <div v-if="isCreate">
         <p class="text-muted">This type of penetration testing is good for testing your cloud servers/infrastructure or
            your office network.</p>

         <VueMultiselect id="extIntType" v-model="networkInfra.extIntType" @blur="v$.networkInfra.extIntType.$touch"
            class="mt-2" :options="externalInternalList" :multiple="true" :close-on-select="true" label="name"
            track-by="value" :searchable="false" placeholder="Please choose at least one..." :disabled="disableForm()">
         </VueMultiselect>

         <span class="form-text text-danger error-msg" v-if="v$.networkInfra.extIntType.$error">
            Please choose at least one.
         </span>

      </div>

      <div v-else>
         <input type="text" class="form-control" :disabled="disableForm()"
            :value="networkInfra.extIntType.map(os => os.name).toString().replace(',', ', ')" />
      </div>

   </div>

   <div class="pentest-field-group">
      <label for="networkPurpose" class="form-label">What is the purpose of these networks?</label>
      <textarea id="networkPurpose" class="form-control" v-model="networkInfra.networkPurpose"
         :disabled="disableForm()"></textarea>
   </div>

   <div class="pentest-field-group">
      <label for="ipDomainSubnetDetails" class="form-label">Please provide the list of IPs/ domains and or subnets in
         scope for testing.
         A count of these is also sufficient.
         <span v-if="isCreate" class="requiredField">*</span>
      </label>
      <textarea id="ipDomainSubnetDetails" class="form-control" v-model="networkInfra.ipDomainSubnetDetails"
         @blur="v$.networkInfra.ipDomainSubnetDetails.$touch" :disabled="disableForm()"></textarea>

      <span class="form-text text-danger error-msg" v-if="v$.networkInfra.ipDomainSubnetDetails.$error">
         Required field.
      </span>
   </div>

   <div v-if="isCreate">
      <label for="netInfraRelevantFiles" class="form-label">Upload any files related to the previous question
         here.</label>
      <FileUploader ref="netInfraRelevantFiles" :id="'netInfraRelevantFiles'" @fileListChange="handleFileUpload"
         key="netInfraRelevantFiles" />
   </div>

   <div class="form-control pentest-field-group">
      <label class="form-label">Will this be an authenticated test with logins provided?
         <span v-if="isCreate" class="requiredField">*</span>
      </label>

      <div v-if="isCreate">

         <p class="text-muted">Authenticated tests generally provide more comprehensive and valuable results as it
            enables us to test for vulnerabilities outside and inside the website.</p>
         <div class="mb-2">

            <div class="form-check form-check-inline">
               <input class="form-check-input" type="radio" :value=true v-model.trim="networkInfra.isAuthenticated"
                  id="isAuthenticated" name="isAuthenticatedNetwork" :disabled="disableForm()">
               <label class="form-check-label" for="isAuthenticated">Yes</label>
            </div>

            <div class="form-check form-check-inline">
               <input class="form-check-input" type="radio" :value=false v-model.trim="networkInfra.isAuthenticated"
                  id="notAuthenticated" name="isAuthenticatedNetwork" :disabled="disableForm()">
               <label class="form-check-label" for="notAuthenticated">No</label>
            </div>

         </div>
      </div>

      <div v-else>
         <input type="text" class="form-control" :disabled="disableForm()"
            :value="networkInfra.isAuthenticated ? 'Yes' : 'No'" />
      </div>

   </div>

   <div class="form-control pentest-field-group">
      <label class="form-label">Is testing required from an onsite location?
         <span v-if="isCreate" class="requiredField">*</span>
      </label>
      <div v-if="isCreate" class="mb-2">

         <div class="form-check form-check-inline">
            <input class="form-check-input" type="radio" :value=true v-model.trim="networkInfra.isOnsiteLocation"
               id="isOnsiteLocation" name="isOnsiteLocation" :disabled="disableForm()">
            <label class="form-check-label" for="isOnsiteLocation">Yes</label>
         </div>

         <div class="form-check form-check-inline">
            <input class="form-check-input" type="radio" :value=false v-model.trim="networkInfra.isOnsiteLocation"
               id="notOnsiteLocation" name="isOnsiteLocation" @change="onChange()" :disabled="disableForm()">
            <label class="form-check-label" for="notOnsiteLocation">No</label>
         </div>

      </div>

      <div v-else>
         <input type="text" :disabled="disableForm()" class="form-control" :value="networkInfra.isOnsiteLocation ? 'Yes' : 'No'" />
      </div>

      <div v-if="networkInfra.isOnsiteLocation">
         <label for="onsiteLocation" class="form-label">Where is the onsite location?
            <span v-if="isCreate" class="requiredField">*</span>
         </label>
         <input type="text" id="onsiteLocation" class="form-control" v-model.trim="networkInfra.onsiteLocation"
            @blur="v$.networkInfra.onsiteLocation.$touch" :disabled="disableForm()" />

         <span class="form-text text-danger error-msg" v-if="v$.networkInfra.onsiteLocation.$error">
            Required field.
         </span>
      </div>

   </div>

   <div class="pentest-field-group" v-if="networkInfra.isOnsiteLocation">
      <label for="physicalConnCount" class="form-label">How many physical connections are required to access
         each IP, subnet and/or network which is in scope?
         <span v-if="isCreate" class="requiredField">*</span>
      </label>
      <input type="text" id="physicalConnCount" class="form-control" v-model.trim="networkInfra.physicalConnectionCount"
         @blur="v$.networkInfra.physicalConnectionCount.$touch" :disabled="disableForm()" />

      <span class="form-text text-danger error-msg" v-if="v$.networkInfra.physicalConnectionCount.$error">
         Required field.
      </span>
   </div>

   <div v-if="networkInfra.isOnsiteLocation" class="form-control pentest-field-group">
      <label class="form-label">Does Wifi also need to be penetration tested?
         <span v-if="isCreate" class="requiredField">*</span>
      </label>
      <div v-if="isCreate" class="mb-2">

         <div class="form-check form-check-inline">
            <input class="form-check-input" type="radio" :value=true v-model.trim="networkInfra.isWifiTestingNeeded"
               id="wifiTestingNeeded" name="isWifiTestingNeeded" :disabled="disableForm()">
            <label class="form-check-label" for="wifiTestingNeeded">Yes</label>
         </div>

         <div class="form-check form-check-inline">
            <input class="form-check-input" type="radio" :value=false v-model.trim="networkInfra.isWifiTestingNeeded"
               id="noWifiTestingNeeded" name="isWifiTestingNeeded" @change="onChange()" :disabled="disableForm()">
            <label class="form-check-label" for="noWifiTestingNeeded">No</label>
         </div>

      </div>

      <div v-else class="mb-3">
         <input type="text" :disabled="disableForm()" class="form-control" :value="networkInfra.isWifiTestingNeeded ? 'Yes' : 'No'" />
      </div>

      <div v-if="networkInfra.isWifiTestingNeeded">
         <label for="ssidDetails" class="form-label">Please provide the SSIDs in scope.
            <span v-if="isCreate" class="requiredField">*</span>
         </label>
         <textarea id="ssidDetails" class="form-control" v-model.trim="networkInfra.ssidDetails"
            @blur="v$.networkInfra.ssidDetails.$touch" :disabled="disableForm()"></textarea>

         <span class="form-text text-danger error-msg" v-if="v$.networkInfra.ssidDetails.$error">
            Required field.
         </span>
      </div>

   </div>

   <!-- files list -->
   <div class="pentest-field-group pentest-files-list" v-if="isView && networkInfra.relevantFiles">
      <div class="mb-2 align-items-center">
         <strong>Files provided:</strong>
         <a href="#" class="m-2 small" v-if="networkInfra.relevantFiles.length > 1 && !isDownloadingAllFiles"
            @click="downloadAllFiles('NetworkInfrastructure', this.testTypeId)"
            title="Click to download all available files" style="color:gray">
            <i class="fa-solid fa-download pe-1"></i>
            <span>Download all files</span>
         </a>

         <span class="m-2 small" v-if="isDownloadingAllFiles" :class="{ 'd-none': !isDownloadingAllFiles }"
            style="color:gray">
            <span class="spinner-border spinner-border-sm" role="status">
               <span class="visually-hidden">Downloading files...</span>
            </span>

            <span class="m-2">Downloading files...</span>
         </span>
      </div>

      <ul class="pb-2">
         <li v-for="file in networkInfra.relevantFiles" :key="file.id">

            <div class="file-wrapper">
               <div class="pb-3 file-icon">
                  <img :src="require('@/assets/img/file_type_icons/' + getFileTypeIcon(file.file_extension))"
                     alt="File Icon" />
               </div>

               <span class="file-download-icon" title="Click to download file"
                  @click="downloadFile('NetworkInfrastructure', file)">
                  <i class="fa-regular fa-circle-down"></i>
               </span>

               <div class="pt-3 file-name" :title="file.file_name">{{ file.file_name }}</div>
            </div>

            <span :id="file.id" :ref="file.id" class="loading d-none">
               <span class="spinner-border text-dark" role="status">
                  <span class="visually-hidden">Downloading file...</span>
               </span>
            </span>
         </li>

      </ul>

   </div>
   <!-- end -- files list -->

</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import { getFileTypeIcon } from '@/plugins/helper.js'
import VueMultiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
import "@/assets/scss/vue-multiselect.scss";
import FileUploader from '@/components/FileUploader.vue';

export default ({

   components: {
      VueMultiselect,
      FileUploader
   },

   setup() {
      return { v$: useVuelidate() }
   },

   emits: ['downloadFile', 'downloadAllFiles'],

   props: {
      items: {
         type: Object,
      },

      action: {
         type: String,
      },

   },

   data() {
      return {
         networkInfra: {
            extIntType: [],
            isInternal: false,
            isExternal: false,
            networkPurpose: '',
            ipDomainSubnetDetails: '',
            relevantFiles: [],
            isAuthenticated: false,
            isOnsiteLocation: false,
            onsiteLocation: '',
            physicalConnectionCount: '',
            isWifiTestingNeeded: false,
            ssidDetails: '',
         },
         externalInternalList: [
            { name: "External", value: "external" },
            { name: "Internal", value: "internal" }
         ],
         testTypeId: '',
         isView: false,
         isCreate: false,
         isDownloadingAllFiles: false,
      }

   },

   validations() {
      return {
         networkInfra: {
            extIntType: { required },
            ipDomainSubnetDetails: { required },
            isAuthenticated: { required },
            isOnsiteLocation: { required },
            onsiteLocation: { requiredIf: requiredIf(this.networkInfra.isOnsiteLocation) },
            physicalConnectionCount: { requiredIf: requiredIf(this.networkInfra.isOnsiteLocation) },
            isWifiTestingNeeded: { requiredIf: requiredIf(this.networkInfra.isOnsiteLocation) },
            ssidDetails: { requiredIf: requiredIf(this.networkInfra.isWifiTestingNeeded) },
         },
      }
   },

   computed: {
      itemList() {
         return this.items
      },

      selectedExtIntType() {
         return this.networkInfra.extIntType
      },
   },

   watch: {
      itemList() {
         this.init()
      },

      selectedExtIntType() {

         let data = this.networkInfra.extIntType

         if (data.length > 0) {
            this.networkInfra.isExternal = data.map(e => (e.value)).includes("external")

            this.networkInfra.isInternal = data.map(e => (e.value)).includes("internal")
         }

      },
   },

   created() {
      this.init()
   },

   methods: {
      init() {

         if (this.action == 'create') {
            this.createForm()
         } else {
            this.viewForm()
            this.testTypeId = this.items.test_type
            this.networkInfra.extIntType = []
            this.networkInfra.isInternal = this.items.network_infra.net_infra_is_internal
            this.networkInfra.isExternal = this.items.network_infra.net_infra_is_external
            this.networkInfra.networkPurpose = this.items.network_infra.net_infra_purpose
            this.networkInfra.ipDomainSubnetDetails = this.items.network_infra.net_infra_ip_domain_subnet_details
            this.networkInfra.relevantFiles = this.items.network_infra.relevant_files
            this.networkInfra.isAuthenticated = this.items.network_infra.net_infra_is_authenticated
            this.networkInfra.isOnsiteLocation = this.items.network_infra.net_infra_is_onsite_testing
            this.networkInfra.onsiteLocation = this.items.network_infra.net_infra_onsite_location
            this.networkInfra.physicalConnectionCount = this.items.network_infra.net_infra_physical_connection_count
            this.networkInfra.isWifiTestingNeeded = this.items.network_infra.net_infra_is_wifi_testing_needed
            this.networkInfra.ssidDetails = this.items.network_infra.net_infra_ssid_details

            if (this.networkInfra.isInternal) {
               this.networkInfra.extIntType.push({
                  name: "Internal", value: "internal",
               })
            }

            if (this.networkInfra.isExternal) {
               this.networkInfra.extIntType.push({
                  name: "External", value: "external",
               })
            }
         }

      },

      createForm() {
         this.isCreate = true
         this.isView = false
      },

      viewForm() {
         this.isCreate = false
         this.isView = true
      },

      // reset some inputs that are dependent on radio button value
      onChange() {
         //INFRA
         if (!this.networkInfra.isOnsiteLocation) {
            this.networkInfra.onsiteLocation = ''
            this.v$.networkInfra.onsiteLocation.$reset()
            this.networkInfra.isWifiTestingNeeded = false
         }

         if (!this.networkInfra.isWifiTestingNeeded) {
            this.networkInfra.ssidDetails = ''
            this.v$.networkInfra.ssidDetails.$reset()
         }
      },

      getNetworkInfraPenTestDetails() {
         let network_infra = {
            net_infra_is_internal: this.networkInfra.isInternal,
            net_infra_is_external: this.networkInfra.isExternal,
            net_infra_purpose: this.networkInfra.networkPurpose,
            net_infra_ip_domain_subnet_details: this.networkInfra.ipDomainSubnetDetails,
            net_infra_is_authenticated: this.networkInfra.isAuthenticated,
            net_infra_is_onsite_testing: this.networkInfra.isOnsiteLocation,
            net_infra_onsite_location: this.networkInfra.onsiteLocation,
            net_infra_physical_connection_count: this.networkInfra.physicalConnectionCount,
            net_infra_is_wifi_testing_needed: this.networkInfra.isWifiTestingNeeded,
            net_infra_ssid_details: this.networkInfra.ssidDetails,
         }

         network_infra.relevant_files = []
         this.networkInfra.relevantFiles.forEach((s) => {
            network_infra.relevant_files.push({
               encoded_file: s.encoded,
               file_extension: s.extension
            })
         })

         return network_infra
      },

      getFileTypeIcon(type) {
         return getFileTypeIcon(type)
      },

      handleFileUpload({ type, files }) {

         if (type == "netInfraRelevantFiles") {
            this.networkInfra.relevantFiles = files
         }

      },

      disableForm() {
         return this.isView
      },

      downloadFile(testType, file) {
         //show loading icon
         this.$refs[file.id][0].classList.remove('d-none')
         this.$emit('downloadFile', { testType: testType, file: file })
      },

      downloadAllFiles(testType, testTypeId) {
         this.isDownloadingAllFiles = true
         this.$emit('downloadAllFiles', { testType: testType, testTypeId: testTypeId })
      },

      downloadFileDone(fileId) {
         //show loading icon
         this.$refs[fileId][0].classList.add('d-none')
      },

      downloadAllFilesDone() {
         this.isDownloadingAllFiles = false
      },
   }
})
</script>
